import { useEffect, useMemo, useState } from "react";

import { useParams, useLocation } from "react-router-dom";

import { ThreeDots as Loader } from "react-loader-spinner";
import ReactPaginate from "react-paginate";

import { useSearchContext } from "../../contexts/SearchContext";
import { useProducts } from "../../hooks/queries/useProducts";
import { useOrder } from "../../hooks/queries/useOrder";
import { useSendItems } from "../../hooks/mutations/useSendItems";
import Product from "../../types/Product";
import OrderItem from "../../types/OrderItem";
import ItemToSend from "../../types/ItemToSend";
import ProductParams from "../../types/ProductParams";

import { setTitle } from "../../services/browser";
import colors from "../../styles/colors";
import { calculateMaxQuantity } from "../../utils/calculateMaxQuantity";
import {
  ButtonsContainer,
  LoaderContainer,
  PaginatorContainer,
} from "../../styles/common";
import { Container } from "./styles";

import BtnPrimary from "../../components/BtnPrimary";
import ProductList from "../../components/ProductList";
import { Box } from "@mui/material";
import ColorsLegend from "../../components/ProductList/components/ColorsLegend";

interface OwnProps {
  title?: string | null;
  params?: ProductParams | Record<string, never>;
  pageSize?: number;
  offset?: number;
  lancamento?: boolean;
  promocao?: boolean;
  onLoaded?: (info: { count: number; isLoading: boolean }) => void;
}

const Products = ({
  title = null,
  params: propsParams = {},
  pageSize = 100,
  offset = 0,
  lancamento = false,
  promocao = false,
  onLoaded,
}: OwnProps) => {
  const routeParams = useParams();
  const location = useLocation();
  const {
    setSearchTerm,
    setSearchIndustry,
    setSearchMixIdeal,
    setSearchCategory,
    setSearchCategoryGroup,
    setSearchProductType,
  } = useSearchContext();

  // Estado local para itens a serem enviados (substitui Redux)
  const [itemsToSend, setItemsToSend] = useState<ItemToSend[]>([]);

  // Use params from props if available (from SearchResult), otherwise from route
  const params = propsParams || routeParams;

  const [currentOffset, setCurrentOffset] = useState(offset);

  // Prepara os parâmetros para a query
  const queryParams = useMemo((): ProductParams => {
    const { term, industry, type, categoryGroup, category, mixIdeal } = params;
    const query = new URLSearchParams(location.search);
    const cod = query.get("cod");

    if (term || industry || mixIdeal || type || categoryGroup || category) {
      const result: ProductParams = {
        offset: currentOffset,
      };
      if (term) result.term = term;
      if (industry) result.industry = industry;
      if (mixIdeal) result.mixIdeal = mixIdeal;
      if (type) result.type = type;
      if (categoryGroup) result.categoryGroup = categoryGroup;
      if (category) result.category = category;
      return result;
    } else if (lancamento || promocao || cod) {
      const result: ProductParams = {
        offset: currentOffset,
      };
      if (lancamento) result.lancamento = lancamento;
      if (promocao) result.promocao = promocao;
      if (cod) result.cod = cod;
      return result;
    } else {
      return {
        offset: currentOffset,
      };
    }
  }, [
    params.term,
    params.industry,
    params.type,
    params.categoryGroup,
    params.category,
    params.mixIdeal,
    currentOffset,
    lancamento,
    promocao,
    location.search,
  ]);

  const {
    data: productsData,
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
  } = useProducts(queryParams);

  useEffect(() => {
    onLoaded?.({
      count: productsData?.count ?? 0,
      isLoading: isLoadingProducts || isFetchingProducts,
    });
  }, [productsData?.count, isLoadingProducts, isFetchingProducts]);

  const products = productsData?.rows || [];
  const total = productsData?.count || 0;

  // Busca dados do pedido via React Query
  const { data: orderData } = useOrder();
  const itemsOnOrder = (orderData?.items || []) as OrderItem[];

  // Hook de mutation para adicionar itens
  const sendItemsMutation = useSendItems();

  // Removido: atualização do cache via Redux não é mais necessária

  useEffect(() => {
    const { term, industry, mixIdeal, type, categoryGroup, category } = params;
    if (term || industry || mixIdeal || type || categoryGroup || category) {
      // industries são carregadas automaticamente pelo hook useIndustries quando necessário
      setSearchTerm(term || "*", currentOffset);
      setSearchIndustry(industry || "*");
      setSearchMixIdeal(mixIdeal || "*");
      setSearchProductType(type || "*");
      setSearchCategoryGroup(categoryGroup || "*");
      setSearchCategory(category || "*");
    }
  }, [
    params.term,
    params.industry,
    params.type,
    params.categoryGroup,
    params.category,
    params.mixIdeal,
    currentOffset,
    setSearchTerm,
    setSearchIndustry,
    setSearchMixIdeal,
    setSearchProductType,
    setSearchCategoryGroup,
    setSearchCategory,
  ]);

  // Reset offset quando os parâmetros de busca mudam
  useEffect(() => {
    setCurrentOffset(0);
  }, [
    params.term,
    params.industry,
    params.type,
    params.categoryGroup,
    params.category,
    params.mixIdeal,
  ]);

  const setQt = (product: Product, qtd: number) => {
    if (!product.infos) {
      return; // Produto sem informações de estoque não pode ser adicionado
    }

    const maxQtd = calculateMaxQuantity(qtd, product.infos, product.bloqueio);

    const newProduct: ItemToSend = {
      cd_mercadoria: product.cd_mercadoria,
      full_cd_mercadoria:
        product.full_cd_mercadoria || String(product.cd_mercadoria),
      qtd: maxQtd,
      infos: product.infos,
      bloqueio: product.bloqueio ?? undefined,
    };

    const updatedItemsToSend = itemsToSend.filter(
      item => product.cd_mercadoria !== item.cd_mercadoria
    );

    if (newProduct.qtd > 0) {
      updatedItemsToSend.push(newProduct);
    }
    setItemsToSend(updatedItemsToSend);
  };

  const getAddButtonText = () => {
    return !itemsToSend || (itemsToSend && itemsToSend.length === 0)
      ? "Nenhum item para adicionar"
      : `Adicionar ${itemsToSend.length} ite${
          itemsToSend.length > 1 ? "ns" : "m"
        } no pedido`;
  };

  const sendItems = async (event: React.FormEvent) => {
    event.preventDefault();

    const arrPrepared: {
      mercadoria: string;
      qtd: number;
      placeholder: number;
    }[] = [];
    itemsToSend.forEach(item => {
      arrPrepared.push({
        mercadoria: item.full_cd_mercadoria,
        qtd: parseInt(String(item.qtd), 10),
        placeholder: parseInt(String(item.placeholder), 10) || 0,
      });
    });

    // Reset parcelamento é feito automaticamente pelo hook useParcelamento quando os totais mudam

    // Usa o hook de mutation do React Query
    sendItemsMutation.mutate(
      { items: arrPrepared, sum: true },
      {
        onSuccess: () => {
          // Limpa os itens a serem enviados após sucesso
          setItemsToSend([]);
        },
      }
    );
  };

  const changePage = (data: { selected: number }) => {
    const pageNumber = data.selected; // Inicia em 0
    const totalPagesCount = Math.ceil(total / pageSize) - 1;
    if (pageNumber >= 0 && pageNumber <= totalPagesCount) {
      const newOffset = pageSize * pageNumber;
      setCurrentOffset(newOffset);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    setTitle(title || "Bem vindo!");
  }, [title]);

  return (
    <Container onSubmit={sendItems}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <h1>{title || "Produtos"}</h1>

        {!promocao && !lancamento && <ColorsLegend />}
      </Box>

      <ProductList
        products={products}
        loading={isLoadingProducts}
        onQtChange={setQt}
        itemsOnOrder={itemsOnOrder}
        itemsToSend={itemsToSend}
        rowHighlight={!promocao && !lancamento}
      />

      <PaginatorContainer>
        <ReactPaginate
          previousLabel="Anterior"
          nextLabel="Próximo"
          marginPagesDisplayed={1}
          breakLabel="..."
          pageCount={totalPages}
          onPageChange={changePage}
          pageRangeDisplayed={5}
          containerClassName="pagination"
        />
      </PaginatorContainer>

      <ButtonsContainer>
        <p>
          <BtnPrimary
            type="submit"
            disabled={!itemsToSend || (itemsToSend && itemsToSend.length === 0)}
          >
            {sendItemsMutation.isPending ? (
              <LoaderContainer>
                <Loader color={colors.loader.light} height={15} width={30} />
                <span>enviando...</span>
              </LoaderContainer>
            ) : (
              getAddButtonText()
            )}
          </BtnPrimary>
        </p>
      </ButtonsContainer>
    </Container>
  );
};

export default Products;
